import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TableHead } from './ui/table';

/**
 * Drop-in replacement for <TableHead> that supports drag-to-resize and persists
 * column widths in localStorage. Pass a stable `storageKey` per table+column.
 *
 *   <ResizableTableHead storageKey="txns:date" defaultWidth={120}>Date</ResizableTableHead>
 *
 * On the parent <table>, set `style={{ tableLayout: 'fixed' }}` so explicit
 * widths are respected.
 */
export function ResizableTableHead({
  children,
  storageKey,
  defaultWidth = 120,
  minWidth = 60,
  maxWidth = 1200,
  className = '',
  onSort,
  sortable = false,
  sortKey,
  sortState,
  ...rest
}) {
  const initial = (() => {
    try {
      const v = parseInt(localStorage.getItem(`colw:${storageKey}`), 10);
      return Number.isFinite(v) && v > 0 ? v : defaultWidth;
    } catch { return defaultWidth; }
  })();
  const [width, setWidth] = useState(initial);
  const startX = useRef(0);
  const startWidth = useRef(initial);
  const dragging = useRef(false);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - startX.current;
    const next = Math.min(maxWidth, Math.max(minWidth, startWidth.current + dx));
    setWidth(next);
  }, [minWidth, maxWidth]);

  const onMouseUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try { localStorage.setItem(`colw:${storageKey}`, String(startWidth.current)); } catch {}
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  }, [storageKey, onMouseMove]);

  // Persist final width when state settles
  useEffect(() => {
    if (!dragging.current) {
      try { localStorage.setItem(`colw:${storageKey}`, String(width)); } catch {}
    }
    startWidth.current = width;
  }, [width, storageKey]);

  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const onDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setWidth(defaultWidth);
  };

  const sortIndicator = sortable && sortState?.key === sortKey
    ? (sortState.dir === 'asc' ? ' ▲' : ' ▼')
    : (sortable ? ' ⇅' : '');

  const headerLabelStyle = {
    cursor: sortable ? 'pointer' : 'default',
    userSelect: 'none',
    display: 'inline-block',
    width: '100%',
  };

  return (
    <TableHead
      {...rest}
      style={{ width, minWidth: width, maxWidth: width, position: 'relative', ...(rest.style || {}) }}
      className={`${className} group`}
    >
      <span
        style={headerLabelStyle}
        onClick={sortable && onSort ? () => onSort(sortKey) : undefined}
      >
        {children}{sortIndicator}
      </span>
      <span
        role="separator"
        aria-orientation="vertical"
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
        title="Drag to resize · double-click to reset"
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.55))' }}
        data-testid={`resize-handle-${storageKey}`}
      />
    </TableHead>
  );
}
